import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../context/ThemeContext';
import { getTheme } from '../../theme';
import * as Icons from '@mui/icons-material';
import { 
  Search, 
  Close,
  CheckCircle
} from '@mui/icons-material';

const IconPickerContainer = styled.div`
  position: relative;
`;

const IconInputWrapper = styled.div`
  display: flex;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
  align-items: center;
`;

const IconInput = styled.input`
  flex: 1;
  padding: ${props => getTheme(props.theme?.isDark).spacing.md};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.surface};
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  font-family: ${props => getTheme(props.theme?.isDark).fonts.primary};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => getTheme(props.theme?.isDark).colors.primary};
    ${props => props.theme?.isDark && `
      box-shadow: 0 0 10px rgba(25, 184, 136, 0.3);
    `}
  }
`;

const IconButton = styled.button`
  padding: ${props => getTheme(props.theme?.isDark).spacing.md};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.surface};
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  min-width: 48px;
  height: 48px;

  &:hover {
    background-color: ${props => getTheme(props.theme?.isDark).colors.hover};
    border-color: ${props => getTheme(props.theme?.isDark).colors.primary};
  }
`;

const SelectedIconPreview = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.surface};
  color: ${props => getTheme(props.theme?.isDark).colors.text};
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${props => getTheme(props.theme?.isDark).spacing.lg};
`;

const ModalContent = styled.div`
  background-color: ${props => getTheme(props.theme?.isDark).colors.surface};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.lg};
  width: 100%;
  max-width: 800px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  padding: ${props => getTheme(props.theme?.isDark).spacing.lg};
  border-bottom: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  font-size: 18px;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  cursor: pointer;
  padding: ${props => getTheme(props.theme?.isDark).spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.sm};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => getTheme(props.theme?.isDark).colors.hover};
  }
`;

const SearchContainer = styled.div`
  padding: ${props => getTheme(props.theme?.isDark).spacing.md} ${props => getTheme(props.theme?.isDark).spacing.lg};
  border-bottom: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${props => getTheme(props.theme?.isDark).spacing.md};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.surface};
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  font-family: ${props => getTheme(props.theme?.isDark).fonts.primary};
  padding-left: 40px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => getTheme(props.theme?.isDark).colors.primary};
    ${props => props.theme?.isDark && `
      box-shadow: 0 0 10px rgba(25, 184, 136, 0.3);
    `}
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: ${props => getTheme(props.theme?.isDark).spacing.lg};
  top: 50%;
  transform: translateY(-50%);
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  pointer-events: none;
`;

const IconsGrid = styled.div`
  padding: ${props => getTheme(props.theme?.isDark).spacing.lg};
  overflow-y: auto;
  flex: 1;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const IconItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => getTheme(props.theme?.isDark).spacing.md};
  border: 2px solid ${props => 
    props.selected 
      ? getTheme(props.theme?.isDark).colors.primary 
      : getTheme(props.theme?.isDark).colors.border
  };
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${props => 
    props.selected 
      ? `${getTheme(props.theme?.isDark).colors.primary}15`
      : 'transparent'
  };
  position: relative;

  &:hover {
    border-color: ${props => getTheme(props.theme?.isDark).colors.primary};
    background-color: ${props => getTheme(props.theme?.isDark).colors.hover};
    transform: translateY(-2px);
    ${props => props.theme?.isDark && `
      box-shadow: 0 4px 12px rgba(25, 184, 136, 0.2);
    `}
  }
`;

const IconName = styled.div`
  font-size: 10px;
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  margin-top: ${props => getTheme(props.theme?.isDark).spacing.xs};
  text-align: center;
  word-break: break-word;
  max-width: 100%;
`;

const SelectedBadge = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  background-color: ${props => getTheme(props.theme?.isDark).colors.primary};
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => getTheme(props.theme?.isDark).spacing.xxl};
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
`;

// Popular Material-UI icons list
const POPULAR_ICONS = [
  'CheckCircle', 'CheckCircleOutline', 'Check',
  'ArrowForward', 'ArrowBack', 'ArrowUp', 'ArrowDown', 'ArrowRight', 'ArrowLeft',
  'Add', 'Remove', 'Delete', 'Edit', 'Save', 'Cancel', 'Close',
  'Home', 'Settings', 'Menu', 'Notifications', 'Email', 'Phone',
  'Star', 'Favorite', 'FavoriteBorder', 'Search', 'Filter',
  'Info', 'Warning', 'Error', 'ErrorOutline',
  'Person', 'People', 'AccountCircle', 'Lock', 'LockOpen',
  'Visibility', 'VisibilityOff', 'Refresh', 'Download', 'Upload',
  'PlayArrow', 'Pause', 'Stop', 'SkipNext', 'SkipPrevious',
  'ThumbUp', 'ThumbDown', 'Share', 'MoreVert', 'MoreHoriz',
  'CalendarToday', 'Schedule', 'LocationOn', 'Map',
  'ShoppingCart', 'Payment', 'CreditCard', 'Receipt',
  'Restaurant', 'LocalDining', 'FitnessCenter', 'DirectionsRun',
  'WaterDrop', 'LocalDrink', 'Fastfood', 'Cake',
  'TrendingUp', 'TrendingDown', 'BarChart', 'PieChart',
  'Help', 'HelpOutline', 'QuestionAnswer', 'Support',
  'Done', 'DoneAll', 'Clear', 'Backspace',
  'Send', 'Reply', 'Forward', 'Archive',
  'Folder', 'FolderOpen', 'InsertDriveFile', 'Description',
  'Image', 'Photo', 'VideoLibrary', 'MusicNote',
  'CloudUpload', 'CloudDownload', 'Cloud', 'Storage',
  'Wifi', 'Bluetooth', 'SignalWifi', 'BatteryFull',
  'Brightness', 'DarkMode', 'LightMode', 'Palette',
  'Language', 'Translate', 'Public', 'Globe',
  'Security', 'Shield', 'Verified', 'VerifiedUser',
  'School', 'Book', 'LibraryBooks', 'MenuBook',
  'Work', 'Business', 'Store', 'ShoppingBag',
  'Flight', 'Train', 'DirectionsCar', 'DirectionsBus',
  'Hotel', 'RestaurantMenu', 'LocalCafe', 'LocalBar',
  'Sports', 'SportsSoccer', 'SportsBasketball', 'SportsTennis',
  'Music', 'Headphones', 'Radio', 'Podcasts',
  'Movie', 'Theaters', 'LiveTv', 'Tv',
  'Computer', 'Laptop', 'Phone', 'Tablet',
  'Watch', 'AccessTime', 'Alarm', 'Timer',
  'Event', 'EventAvailable', 'EventBusy', 'Today',
  'Week', 'Month', 'Year', 'DateRange',
  'NotificationsActive', 'NotificationsOff', 'NotificationImportant',
  'Chat', 'ChatBubble', 'ChatBubbleOutline', 'Forum',
  'Message', 'MessageOutline', 'Sms', 'Comment',
  'Inbox', 'Drafts', 'Sent', 'Mail',
  'MarkEmailRead', 'MarkEmailUnread', 'EmailOutline',
  'Call', 'CallMade', 'CallReceived', 'CallMissed',
  'VideoCall', 'Videocam', 'VideocamOff', 'Camera',
  'CameraAlt', 'PhotoCamera', 'CameraRoll', 'ImageSearch',
  'Crop', 'Filter', 'FilterList', 'Tune',
  'Sort', 'SortByAlpha', 'SwapVert', 'SwapHoriz',
  'List', 'GridOn', 'ViewList', 'ViewModule',
  'Fullscreen', 'FullscreenExit', 'ZoomIn', 'ZoomOut',
  'ExpandMore', 'ExpandLess', 'ChevronRight', 'ChevronLeft',
  'KeyboardArrowUp', 'KeyboardArrowDown', 'KeyboardArrowRight', 'KeyboardArrowLeft',
  'FirstPage', 'LastPage', 'NavigateBefore', 'NavigateNext',
  'Home', 'Dashboard', 'Apps', 'ViewQuilt',
  'AccountBox', 'PersonOutline', 'SupervisorAccount', 'Group',
  'Lock', 'LockOpen', 'VpnKey', 'Password',
  'Visibility', 'VisibilityOff', 'RemoveRedEye', 'Eye',
  'Settings', 'Tune', 'Build', 'Construction',
  'Help', 'HelpOutline', 'Info', 'InfoOutline',
  'Warning', 'Error', 'ErrorOutline', 'ReportProblem',
  'CheckCircle', 'CheckCircleOutline', 'Cancel', 'CancelOutline',
  'AddCircle', 'RemoveCircle', 'AddCircleOutline', 'RemoveCircleOutline',
  'Delete', 'DeleteOutline', 'DeleteForever', 'DeleteSweep',
  'Edit', 'EditOutline', 'ModeEdit', 'Create',
  'Save', 'SaveAlt', 'SaveOutline', 'Bookmark',
  'BookmarkBorder', 'BookmarkOutline', 'Star', 'StarBorder',
  'Favorite', 'FavoriteBorder', 'ThumbUp', 'ThumbDown',
  'ThumbUpOutline', 'ThumbDownOutline', 'Like', 'Dislike',
  'Share', 'ShareOutline', 'IosShare', 'Forward',
  'Reply', 'ReplyAll', 'Send', 'SendOutline',
  'MoreVert', 'MoreHoriz', 'Menu', 'MenuOpen',
  'Close', 'Clear', 'Backspace', 'ArrowBack',
  'ArrowForward', 'ArrowUpward', 'ArrowDownward', 'ArrowRight',
  'ArrowLeft', 'ArrowDropDown', 'ArrowDropUp', 'ArrowDropDownCircle',
  'Check', 'CheckBox', 'CheckBoxOutlineBlank', 'RadioButtonChecked',
  'RadioButtonUnchecked', 'ToggleOn', 'ToggleOff', 'Switch',
  'PlayArrow', 'Pause', 'Stop', 'SkipNext',
  'SkipPrevious', 'FastForward', 'FastRewind', 'Replay',
  'VolumeUp', 'VolumeDown', 'VolumeOff', 'VolumeMute',
  'Shuffle', 'Repeat', 'RepeatOne', 'ShuffleOn',
  'Search', 'SearchOff', 'FindInPage', 'FindReplace',
  'Filter', 'FilterList', 'FilterAlt', 'Tune',
  'Sort', 'SortByAlpha', 'SortByAlphaAsc', 'SortByAlphaDesc',
  'SwapVert', 'SwapHoriz', 'SwapVerticalCircle', 'SwapHorizontalCircle',
  'List', 'GridOn', 'ViewList', 'ViewModule',
  'ViewHeadline', 'ViewAgenda', 'ViewWeek', 'ViewDay',
  'ViewColumn', 'ViewQuilt', 'ViewComfy', 'ViewCompact',
  'Fullscreen', 'FullscreenExit', 'ZoomIn', 'ZoomOut',
  'Crop', 'CropFree', 'CropOriginal', 'CropRotate',
  'Transform', 'RotateLeft', 'RotateRight', 'Flip',
  'Brightness', 'BrightnessHigh', 'BrightnessLow', 'BrightnessMedium',
  'Contrast', 'Exposure', 'AutoFixHigh', 'AutoFixNormal',
  'Palette', 'ColorLens', 'InvertColors', 'Gradient',
  'Blur', 'BlurOn', 'BlurOff', 'BlurCircular',
  'Filter', 'Filter1', 'Filter2', 'Filter3',
  'Filter4', 'Filter5', 'Filter6', 'Filter7',
  'Filter8', 'Filter9', 'Filter9Plus', 'FilterBAndW',
  'FilterCenterFocus', 'FilterDrama', 'FilterFrames', 'FilterHdr',
  'FilterNone', 'FilterTiltShift', 'FilterVintage', 'Grain',
  'Looks', 'LooksOne', 'LooksTwo', 'Looks3',
  'Looks4', 'Looks5', 'Looks6', 'Photo',
  'PhotoCamera', 'PhotoLibrary', 'PhotoSizeSelectActual', 'PhotoSizeSelectLarge',
  'PhotoSizeSelectSmall', 'PictureAsPdf', 'Portrait', 'Camera',
  'CameraAlt', 'CameraEnhance', 'CameraFront', 'CameraRear',
  'CameraRoll', 'Cameraswitch', 'CenterFocusStrong', 'CenterFocusWeak',
  'Collections', 'CollectionsBookmark', 'Colorize', 'Compare',
  'ControlPoint', 'ControlPointDuplicate', 'Crop', 'Crop169',
  'Crop32', 'Crop54', 'Crop75', 'CropDin',
  'CropFree', 'CropLandscape', 'CropOriginal', 'CropPortrait',
  'CropRotate', 'CropSquare', 'Dehaze', 'Details',
  'Edit', 'Exposure', 'ExposureNeg1', 'ExposureNeg2',
  'ExposurePlus1', 'ExposurePlus2', 'ExposureZero', 'Filter',
  'Filter1', 'Filter2', 'Filter3', 'Filter4',
  'Filter5', 'Filter6', 'Filter7', 'Filter8',
  'Filter9', 'Filter9Plus', 'FilterBAndW', 'FilterCenterFocus',
  'FilterDrama', 'FilterFrames', 'FilterHdr', 'FilterNone',
  'FilterTiltShift', 'FilterVintage', 'Flare', 'FlashAuto',
  'FlashOff', 'FlashOn', 'Flip', 'FlipCameraAndroid',
  'FlipCameraIos', 'Gradient', 'Grain', 'GridOff',
  'GridOn', 'HdrOff', 'HdrOn', 'HdrStrong',
  'HdrWeak', 'Healing', 'Image', 'ImageAspectRatio',
  'ImageNotSupported', 'ImageSearch', 'Iso', 'Landscape',
  'LeakAdd', 'LeakRemove', 'Lens', 'LinkedCamera',
  'Looks', 'Looks3', 'Looks4', 'Looks5',
  'Looks6', 'LooksOne', 'LooksTwo', 'Loupe',
  'MonochromePhotos', 'MovieCreation', 'MovieFilter', 'MusicNote',
  'Nature', 'NaturePeople', 'NavigateBefore', 'NavigateNext',
  'Palette', 'Panorama', 'PanoramaFishEye', 'PanoramaHorizontal',
  'PanoramaVertical', 'PanoramaWideAngle', 'Photo', 'PhotoAlbum',
  'PhotoCamera', 'PhotoFilter', 'PhotoLibrary', 'PhotoSizeSelectActual',
  'PhotoSizeSelectLarge', 'PhotoSizeSelectSmall', 'PictureAsPdf', 'Portrait',
  'RemoveRedEye', 'RotateLeft', 'RotateRight', 'Slideshow',
  'Straighten', 'Style', 'SwitchCamera', 'SwitchVideo',
  'TagFaces', 'Texture', 'Timelapse', 'Timer',
  'Timer10', 'Timer3', 'TimerOff', 'Tonality',
  'Transform', 'Tune', 'ViewComfy', 'ViewCompact',
  'Vignette', 'WbAuto', 'WbCloudy', 'WbIncandescent',
  'WbIridescent', 'WbSunny', 'Add', 'AddBox',
  'AddCircle', 'AddCircleOutline', 'AddLocation', 'AddShoppingCart',
  'AddTask', 'AddToDrive', 'AddToHomeScreen', 'AddToPhotos',
  'AddToQueue', 'Adjust', 'Airplay', 'Album',
  'AllInclusive', 'AllOut', 'Android', 'Announcement',
  'Apps', 'Archive', 'ArrowBack', 'ArrowBackIos',
  'ArrowDownward', 'ArrowDropDown', 'ArrowDropDownCircle', 'ArrowDropUp',
  'ArrowForward', 'ArrowForwardIos', 'ArrowLeft', 'ArrowRight',
  'ArrowUpward', 'ArtTrack', 'AspectRatio', 'Assessment',
  'Assignment', 'AssignmentInd', 'AssignmentLate', 'AssignmentReturn',
  'AssignmentReturned', 'AssignmentTurnedIn', 'Autorenew', 'Backup',
  'Book', 'Bookmark', 'BookmarkBorder', 'Bookmarks',
  'BugReport', 'Build', 'Cached', 'Camera',
  'CameraEnhance', 'CardGiftcard', 'CardMembership', 'CardTravel',
  'ChangeHistory', 'Check', 'CheckBox', 'CheckBoxOutlineBlank',
  'CheckCircle', 'CheckCircleOutline', 'ChevronLeft', 'ChevronRight',
  'Class', 'Close', 'Cloud', 'CloudCircle',
  'CloudDone', 'CloudDownload', 'CloudOff', 'CloudQueue',
  'CloudUpload', 'Code', 'CompareArrows', 'Copyright',
  'Create', 'CreateNewFolder', 'CreditCard', 'Crop',
  'CropFree', 'CropOriginal', 'CropRotate', 'CropSquare',
  'Dashboard', 'DataUsage', 'DateRange', 'Dehaze',
  'Delete', 'DeleteForever', 'DeleteOutline', 'DeleteSweep',
  'Description', 'DesktopMac', 'DesktopWindows', 'Details',
  'DeveloperBoard', 'DeveloperMode', 'DeviceHub', 'Devices',
  'DevicesOther', 'DialerSip', 'Dialpad', 'Directions',
  'DirectionsBike', 'DirectionsBoat', 'DirectionsBus', 'DirectionsCar',
  'DirectionsRailway', 'DirectionsRun', 'DirectionsSubway', 'DirectionsTransit',
  'DirectionsWalk', 'DiscFull', 'Dns', 'Dock',
  'Domain', 'Done', 'DoneAll', 'DoneOutline',
  'DonutLarge', 'DonutSmall', 'Drafts', 'DragHandle',
  'DriveEta', 'Dvr', 'Edit', 'EditLocation',
  'Eject', 'Email', 'EnhancedEncryption', 'Equalizer',
  'Error', 'ErrorOutline', 'Event', 'EventAvailable',
  'EventBusy', 'EventNote', 'ExitToApp', 'ExpandLess',
  'ExpandMore', 'Explicit', 'Explore', 'Extension',
  'Face', 'Facebook', 'FactCheck', 'FamilyRestroom',
  'FastForward', 'FastRewind', 'Favorite', 'FavoriteBorder',
  'FeaturedPlayList', 'FeaturedVideo', 'Feedback', 'FiberDvr',
  'FiberManualRecord', 'FiberNew', 'FiberPin', 'FiberSmartRecord',
  'FileCopy', 'FileDownload', 'FileUpload', 'Filter',
  'Filter1', 'Filter2', 'Filter3', 'Filter4',
  'Filter5', 'Filter6', 'Filter7', 'Filter8',
  'Filter9', 'Filter9Plus', 'FilterBAndW', 'FilterCenterFocus',
  'FilterDrama', 'FilterFrames', 'FilterHdr', 'FilterList',
  'FilterNone', 'FilterTiltShift', 'FilterVintage', 'FindInPage',
  'FindReplace', 'Fingerprint', 'FirstPage', 'FitnessCenter',
  'Flag', 'Flare', 'FlashAuto', 'FlashOff',
  'FlashOn', 'Flight', 'FlightLand', 'FlightTakeoff',
  'Flip', 'FlipToBack', 'FlipToFront', 'Folder',
  'FolderOpen', 'FolderShared', 'FolderSpecial', 'FontDownload',
  'FormatAlignCenter', 'FormatAlignJustify', 'FormatAlignLeft', 'FormatAlignRight',
  'FormatBold', 'FormatClear', 'FormatColorFill', 'FormatColorReset',
  'FormatColorText', 'FormatIndentDecrease', 'FormatIndentIncrease', 'FormatItalic',
  'FormatLineSpacing', 'FormatListBulleted', 'FormatListNumbered', 'FormatPaint',
  'FormatQuote', 'FormatShapes', 'FormatSize', 'FormatStrikethrough',
  'FormatTextdirectionLToR', 'FormatTextdirectionRToL', 'FormatUnderlined', 'Forum',
  'Forward', 'Forward10', 'Forward30', 'Forward5',
  'FreeBreakfast', 'Fullscreen', 'FullscreenExit', 'Functions',
  'GTranslate', 'Gamepad', 'Games', 'Gavel',
  'Gesture', 'GetApp', 'Gif', 'GolfCourse',
  'GpsFixed', 'GpsNotFixed', 'GpsOff', 'Grade',
  'Gradient', 'Grain', 'GraphicEq', 'GridOff',
  'GridOn', 'Group', 'GroupAdd', 'GroupWork',
  'Hd', 'HdrOff', 'HdrOn', 'HdrStrong',
  'HdrWeak', 'Headset', 'HeadsetMic', 'Healing',
  'Hearing', 'Help', 'HelpOutline', 'HighQuality',
  'Highlight', 'HighlightOff', 'History', 'Home',
  'HotTub', 'Hotel', 'HourglassEmpty', 'HourglassFull',
  'Http', 'Https', 'Image', 'ImageAspectRatio',
  'ImportContacts', 'ImportExport', 'ImportantDevices', 'Inbox',
  'IndeterminateCheckBox', 'Info', 'InfoOutline', 'Input',
  'InsertChart', 'InsertComment', 'InsertDriveFile', 'InsertEmoticon',
  'InsertInvitation', 'InsertLink', 'InsertPhoto', 'InvertColors',
  'InvertColorsOff', 'Iso', 'Keyboard', 'KeyboardArrowDown',
  'KeyboardArrowLeft', 'KeyboardArrowRight', 'KeyboardArrowUp', 'KeyboardBackspace',
  'KeyboardCapslock', 'KeyboardHide', 'KeyboardReturn', 'KeyboardTab',
  'KeyboardVoice', 'Kitchen', 'Label', 'LabelOutline',
  'Landscape', 'Language', 'Laptop', 'LaptopChromebook',
  'LaptopMac', 'LaptopWindows', 'LastPage', 'Launch',
  'Layers', 'LayersClear', 'LeakAdd', 'LeakRemove',
  'Lens', 'LibraryAdd', 'LibraryBooks', 'LibraryMusic',
  'Lightbulb', 'LightbulbOutline', 'LineStyle', 'LineWeight',
  'LinearScale', 'Link', 'LinkedCamera', 'List',
  'LiveHelp', 'LiveTv', 'LocalActivity', 'LocalAirport',
  'LocalAtm', 'LocalBar', 'LocalCafe', 'LocalCarWash',
  'LocalConvenienceStore', 'LocalDining', 'LocalDrink', 'LocalFlorist',
  'LocalGasStation', 'LocalGroceryStore', 'LocalHospital', 'LocalHotel',
  'LocalLaundryService', 'LocalLibrary', 'LocalMall', 'LocalMovies',
  'LocalOffer', 'LocalParking', 'LocalPharmacy', 'LocalPhone',
  'LocalPizza', 'LocalPlay', 'LocalPostOffice', 'LocalPrintshop',
  'LocalSee', 'LocalShipping', 'LocalTaxi', 'LocationCity',
  'LocationDisabled', 'LocationOff', 'LocationOn', 'LocationSearching',
  'Lock', 'LockOpen', 'LockOutline', 'Looks',
  'Looks3', 'Looks4', 'Looks5', 'Looks6',
  'LooksOne', 'LooksTwo', 'Loop', 'Loupe',
  'LowPriority', 'Loyalty', 'Mail', 'MailOutline',
  'Map', 'Markunread', 'MarkunreadMailbox', 'Memory',
  'Menu', 'MergeType', 'Message', 'Mic',
  'MicNone', 'MicOff', 'Mms', 'ModeComment',
  'ModeEdit', 'MonetizationOn', 'MoneyOff', 'MonochromePhotos',
  'Mood', 'MoodBad', 'More', 'MoreHoriz',
  'MoreVert', 'Motorcycle', 'Mouse', 'MoveToInbox',
  'Movie', 'MovieCreation', 'MovieFilter', 'MultilineChart',
  'MusicNote', 'MusicVideo', 'MyLocation', 'Nature',
  'NaturePeople', 'NavigateBefore', 'NavigateNext', 'Navigation',
  'NearMe', 'NetworkCell', 'NetworkCheck', 'NetworkLocked',
  'NetworkWifi', 'NewReleases', 'NextWeek', 'Nfc',
  'NoEncryption', 'NoSim', 'NotInterested', 'Note',
  'NoteAdd', 'Notifications', 'NotificationsActive', 'NotificationsNone',
  'NotificationsOff', 'NotificationsPaused', 'OfflinePin', 'OndemandVideo',
  'Opacity', 'OpenInBrowser', 'OpenInNew', 'OpenWith',
  'Pages', 'Pageview', 'Palette', 'PanTool',
  'Panorama', 'PanoramaFishEye', 'PanoramaHorizontal', 'PanoramaVertical',
  'PanoramaWideAngle', 'PartyMode', 'Pause', 'PauseCircleFilled',
  'PauseCircleOutline', 'Payment', 'People', 'PeopleOutline',
  'PermCameraMic', 'PermContactCalendar', 'PermDataSetting', 'PermDeviceInformation',
  'PermIdentity', 'PermMedia', 'PermPhoneMsg', 'PermScanWifi',
  'Person', 'PersonAdd', 'PersonOutline', 'PersonPin',
  'PersonPinCircle', 'PersonalVideo', 'Pets', 'Phone',
  'PhoneAndroid', 'PhoneBluetoothSpeaker', 'PhoneCallback', 'PhoneForwarded',
  'PhoneInTalk', 'PhoneIphone', 'PhoneLocked', 'PhoneMissed',
  'PhonePaused', 'Phonelink', 'PhonelinkErase', 'PhonelinkLock',
  'PhonelinkOff', 'PhonelinkRing', 'PhonelinkSetup', 'Photo',
  'PhotoAlbum', 'PhotoCamera', 'PhotoFilter', 'PhotoLibrary',
  'PhotoSizeSelectActual', 'PhotoSizeSelectLarge', 'PhotoSizeSelectSmall', 'PictureAsPdf',
  'PictureInPicture', 'PictureInPictureAlt', 'PieChart', 'PieChartOutlined',
  'PinDrop', 'Place', 'PlayArrow', 'PlayCircleFilled',
  'PlayCircleOutline', 'PlayForWork', 'PlaylistAdd', 'PlaylistAddCheck',
  'PlaylistPlay', 'PlusOne', 'Poll', 'Polymer',
  'Pool', 'PortableWifiOff', 'Portrait', 'Power',
  'PowerInput', 'PowerSettingsNew', 'PregnantWoman', 'PresentToAll',
  'Print', 'PriorityHigh', 'Public', 'Publish',
  'QueryBuilder', 'QuestionAnswer', 'Queue', 'QueueMusic',
  'QueuePlayNext', 'Radio', 'RadioButtonChecked', 'RadioButtonUnchecked',
  'RateReview', 'Receipt', 'RecentActors', 'RecordVoiceOver',
  'Redeem', 'Redo', 'Refresh', 'Remove',
  'RemoveCircle', 'RemoveCircleOutline', 'RemoveFromQueue', 'RemoveRedEye',
  'Reorder', 'Repeat', 'RepeatOne', 'Replay',
  'Replay10', 'Replay30', 'Replay5', 'Reply',
  'ReplyAll', 'Report', 'ReportProblem', 'Restaurant',
  'RestaurantMenu', 'Restore', 'RestorePage', 'RingVolume',
  'Room', 'RoomService', 'Rotate90DegreesCcw', 'RotateLeft',
  'RotateRight', 'RoundedCorner', 'Router', 'Rowing',
  'RssFeed', 'RvHookup', 'Satellite', 'Save',
  'Scanner', 'Schedule', 'School', 'ScreenLockLandscape',
  'ScreenLockPortrait', 'ScreenLockRotation', 'ScreenRotation', 'ScreenShare',
  'SdCard', 'SdStorage', 'Search', 'Security',
  'SelectAll', 'Send', 'SentimentDissatisfied', 'SentimentNeutral',
  'SentimentSatisfied', 'SentimentVeryDissatisfied', 'SentimentVerySatisfied', 'Settings',
  'SettingsApplications', 'SettingsBackupRestore', 'SettingsBluetooth', 'SettingsBrightness',
  'SettingsCell', 'SettingsEthernet', 'SettingsInputAntenna', 'SettingsInputComponent',
  'SettingsInputComposite', 'SettingsInputHdmi', 'SettingsInputSvideo', 'SettingsOverscan',
  'SettingsPhone', 'SettingsPower', 'SettingsRemote', 'SettingsSystemDaydream',
  'SettingsVoice', 'Share', 'Shop', 'ShoppingBasket',
  'ShoppingCart', 'ShortText', 'ShowChart', 'Shuffle',
  'SignalCellular0Bar', 'SignalCellular1Bar', 'SignalCellular2Bar', 'SignalCellular3Bar',
  'SignalCellular4Bar', 'SignalCellularAlt', 'SignalCellularConnectedNoInternet0Bar', 'SignalCellularConnectedNoInternet1Bar',
  'SignalCellularConnectedNoInternet2Bar', 'SignalCellularConnectedNoInternet3Bar', 'SignalCellularConnectedNoInternet4Bar', 'SignalCellularNoSim',
  'SignalCellularNull', 'SignalCellularOff', 'SignalWifi0Bar', 'SignalWifi1Bar',
  'SignalWifi2Bar', 'SignalWifi3Bar', 'SignalWifi4Bar', 'SignalWifi4BarLock',
  'SignalWifiOff', 'SimCard', 'SimCardAlert', 'SkipNext',
  'SkipPrevious', 'Slideshow', 'SlowMotionVideo', 'Smartphone',
  'SmokeFree', 'SmokingRooms', 'Sms', 'Snooze',
  'Sort', 'SortByAlpha', 'Spa', 'SpaceBar',
  'Speaker', 'SpeakerGroup', 'SpeakerNotes', 'SpeakerNotesOff',
  'SpeakerPhone', 'Spellcheck', 'Star', 'StarBorder',
  'StarHalf', 'Stars', 'StayCurrentLandscape', 'StayCurrentPortrait',
  'StayPrimaryLandscape', 'StayPrimaryPortrait', 'Stop', 'StopScreenShare',
  'Storage', 'Store', 'StoreMallDirectory', 'Straighten',
  'Streetview', 'StrikethroughS', 'Style', 'SubdirectoryArrowLeft',
  'SubdirectoryArrowRight', 'Subject', 'Subscriptions', 'Subtitles',
  'SupervisorAccount', 'SurroundSound', 'SwapCalls', 'SwapHoriz',
  'SwapVert', 'SwapVerticalCircle', 'SwitchCamera', 'SwitchVideo',
  'Sync', 'SyncDisabled', 'SyncProblem', 'SystemUpdate',
  'SystemUpdateAlt', 'Tab', 'TabUnselected', 'Tablet',
  'TabletAndroid', 'TabletMac', 'TagFaces', 'TapAndPlay',
  'Terrain', 'TextFields', 'TextFormat', 'Textsms',
  'Texture', 'Theaters', 'ThreeDRotation', 'ThumbDown',
  'ThumbUp', 'ThumbsUpDown', 'TimeToLeave', 'Timelapse',
  'Timeline', 'Timer', 'Timer10', 'Timer3',
  'TimerOff', 'Title', 'Toc', 'Today',
  'Toll', 'Tonality', 'TouchApp', 'Toys',
  'TrackChanges', 'Traffic', 'Train', 'Tram',
  'TransferWithinAStation', 'Transform', 'Translate', 'TrendingDown',
  'TrendingFlat', 'TrendingUp', 'Tune', 'TurnedIn',
  'TurnedInNot', 'Tv', 'Unarchive', 'Undo',
  'UnfoldLess', 'UnfoldMore', 'Update', 'Usb',
  'VerifiedUser', 'VerticalAlignBottom', 'VerticalAlignCenter', 'VerticalAlignTop',
  'Vibration', 'VideoCall', 'VideoLabel', 'VideoLibrary',
  'Videocam', 'VideocamOff', 'VideogameAsset', 'ViewAgenda',
  'ViewArray', 'ViewCarousel', 'ViewColumn', 'ViewComfy',
  'ViewCompact', 'ViewDay', 'ViewHeadline', 'ViewList',
  'ViewModule', 'ViewQuilt', 'ViewStream', 'ViewWeek',
  'Vignette', 'Visibility', 'VisibilityOff', 'VoiceChat',
  'Voicemail', 'VolumeDown', 'VolumeMute', 'VolumeOff',
  'VolumeUp', 'VpnKey', 'VpnLock', 'Wallpaper',
  'Warning', 'Watch', 'WatchLater', 'WbAuto',
  'WbCloudy', 'WbIncandescent', 'WbIridescent', 'WbSunny',
  'Wc', 'Web', 'WebAsset', 'Weekend',
  'Whatshot', 'Widgets', 'Wifi', 'WifiLock',
  'WifiTethering', 'Work', 'WrapText', 'YoutubeSearchedFor',
  'ZoomIn', 'ZoomOut', 'ZoomOutMap'
];

const IconPicker = ({ value, onChange, theme: themeProp }) => {
  const { isDark } = useTheme();
  const theme = themeProp || { isDark };
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Get all available icons from @mui/icons-material
  const availableIcons = useMemo(() => {
    return Object.keys(Icons).filter(iconName => {
      // Filter out non-icon exports
      return iconName[0] === iconName[0].toUpperCase() && 
             typeof Icons[iconName] !== 'string' &&
             Icons[iconName] !== undefined;
    });
  }, []);

  // Combine popular icons with all available icons, remove duplicates
  const allIcons = useMemo(() => {
    const iconSet = new Set([...POPULAR_ICONS, ...availableIcons]);
    return Array.from(iconSet).sort();
  }, [availableIcons]);

  // Filter icons based on search query
  const filteredIcons = useMemo(() => {
    if (!searchQuery.trim()) {
      return allIcons;
    }
    const query = searchQuery.toLowerCase();
    return allIcons.filter(iconName => 
      iconName.toLowerCase().includes(query)
    );
  }, [allIcons, searchQuery]);

  const handleIconSelect = (iconName) => {
    onChange(iconName);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchQuery('');
  };

  const SelectedIcon = value && Icons[value] ? Icons[value] : null;

  return (
    <IconPickerContainer theme={theme}>
      <IconInputWrapper theme={theme}>
        <IconInput
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Icon adı (məs: CheckCircle, ArrowForward)"
          theme={theme}
        />
        {SelectedIcon && (
          <SelectedIconPreview theme={theme}>
            <SelectedIcon />
          </SelectedIconPreview>
        )}
        <IconButton theme={theme} onClick={handleOpen} type="button">
          <Search />
        </IconButton>
      </IconInputWrapper>

      {isOpen && (
        <ModalOverlay theme={theme} onClick={handleClose}>
          <ModalContent theme={theme} onClick={(e) => e.stopPropagation()}>
            <ModalHeader theme={theme}>
              <ModalTitle theme={theme}>Icon Seçin</ModalTitle>
              <CloseButton theme={theme} onClick={handleClose}>
                <Close />
              </CloseButton>
            </ModalHeader>

            <SearchContainer theme={theme}>
              <div style={{ position: 'relative' }}>
                <SearchIconWrapper theme={theme}>
                  <Search />
                </SearchIconWrapper>
                <SearchInput
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Icon axtar..."
                  theme={theme}
                  autoFocus
                />
              </div>
            </SearchContainer>

            <IconsGrid theme={theme}>
              {filteredIcons.length > 0 ? (
                filteredIcons.map((iconName) => {
                  const IconComponent = Icons[iconName];
                  if (!IconComponent) return null;

                  const isSelected = value === iconName;

                  return (
                    <IconItem
                      key={iconName}
                      theme={theme}
                      selected={isSelected}
                      onClick={() => handleIconSelect(iconName)}
                    >
                      {isSelected && (
                        <SelectedBadge theme={theme}>
                          <CheckCircle style={{ fontSize: 14 }} />
                        </SelectedBadge>
                      )}
                      <IconComponent style={{ fontSize: 32 }} />
                      <IconName theme={theme}>{iconName}</IconName>
                    </IconItem>
                  );
                })
              ) : (
                <EmptyState theme={theme}>
                  <p>Icon tapılmadı</p>
                  <small>Başqa axtarış sözü yoxlayın</small>
                </EmptyState>
              )}
            </IconsGrid>
          </ModalContent>
        </ModalOverlay>
      )}
    </IconPickerContainer>
  );
};

export default IconPicker;

